import { defineStore } from "pinia";
import { PageState, usePageStore } from "@rmp/core/stores/composables/page/usePageStore";
import AbortService from "@rmp/core/services/abortService";
import { FormState, useFormStore } from "@rmp/core/stores/composables/form/useFormStore";
import { AuthAppController } from "@rmp/id/api/auth";
import { ApiProfile } from "@rmp/api-id";
import alertService from "@rmp/core/stores/alerts/services/alertService";
import { useSignInStore } from "@rmp/id/stores/signIn";
import { useAppStore } from "@rmp/core/stores/appStore";
import i18n from "@rmp/id/plugins/i18n";

const abortService = new AbortService();
const authAppController = new AuthAppController(abortService);

const page = usePageStore(abortService);
const form = useFormStore();

export interface SelectProfileState extends PageState, FormState {
	profiles: ApiProfile[];
	selectedProfile: ApiProfile | null;
}

const getDefaultState = (): SelectProfileState => {
	return {
		...page.getDefaultPageState(),
		...form.getDefaultState(),
		profiles: [],
		selectedProfile: null
	};
};

export const useSelectProfileStore = defineStore({
	id: "select-profile",
	state: (): SelectProfileState => getDefaultState(),
	getters: {
		...page.getters,
		...form.getters
	},
	actions: {
		...page.actions,
		...form.actions,
		async beforeInitialized() {
			const { profiles } = useSignInStore();

			if(!profiles.length) {
				(window as Window).location = process.env.VUE_APP_BASE_APP_URL;
			}
			
			this.profiles = profiles;
			this.selectedProfile = profiles[0];
		},
		async submit({ id, payload }: { id: string, payload?: any }) {
			this.formSaving = true;

			try {
				let profile = this.profiles.find(x => x.id === id);
				
				if(!profile) throw new Error(i18n.t("errors.profileNotFound").toString());
				
				const { data: { redirectUri } } = await authAppController.signInWithProfile({
					profileId: profile.id,
					signedPayload: payload
				});

				if(!redirectUri) throw new Error(i18n.t("errors.signInWithProfileRedirectUriInvalid").toString());
				
				const { data: { redirectUri: callbackUrl } } = await authAppController.authorize(redirectUri);
				(window as Window).location = callbackUrl;
				
			} catch (error: any) {
				console.error(error);
				
				if(error.detail) {
					alertService.addCustomError(error.detail);
				} else {
					useAppStore().handleActionError(error);
				}
			} finally {
				this.formSaving = false;
			}
		}
	}
});
